import axios from 'axios'
import type {
  ExchangeRateData,
  CoinGeckoHistoryResponse
} from '@/types'

const coinGeckoApi = axios.create({
  baseURL: 'https://api.coingecko.com/api/v3',
  timeout: 15000,
})

// Frankfurter.dev API (European Central Bank) - free, no auth required
const frankfurterApi = axios.create({
  baseURL: 'https://api.frankfurter.dev/v1',
  timeout: 15000,
})

interface FrankfurterResponse {
  amount: number
  base: string
  start_date: string
  end_date: string
  rates: Record<string, Record<string, number>>
}

export class ExchangeRateService {
  private fiatDataCache: Record<string, ExchangeRateData[]> = {}
  private fiatDataCacheTimestamp: number = 0
  private readonly CACHE_DURATION = 5 * 60 * 1000 // 5 minutes in milliseconds

  // Fetch all fiat currencies for a full year in a single call
  private async fetchAllFiatData(): Promise<void> {
    const now = Date.now()

    // Check if cache is still valid
    if (this.fiatDataCacheTimestamp && (now - this.fiatDataCacheTimestamp) < this.CACHE_DURATION) {
      return // Use existing cache
    }

    const endDate = new Date()
    const startDate = new Date()
    startDate.setFullYear(endDate.getFullYear() - 1) // Get 1 year of data

    const endDateStr = endDate.toISOString().split('T')[0]
    const startDateStr = startDate.toISOString().split('T')[0]

    console.log(`Fetching fiat data from ${startDateStr} to ${endDateStr}`)

    // Clear existing cache
    this.fiatDataCache = {}

    try {
      const response = await frankfurterApi.get<FrankfurterResponse>(
        `/${startDateStr}..${endDateStr}`,
        {
          params: {
            from: 'CZK',
            to: 'USD,EUR'
          }
        }
      )

      if (!response.data.rates) {
        throw new Error('Exchange rate service is temporarily unavailable. Please try again later.')
      }

      // Process EUR and USD from Frankfurter
      const ecbCurrencies = ['USD', 'EUR']

      ecbCurrencies.forEach(currency => {
        const currencyData: ExchangeRateData[] = []

        Object.entries(response.data.rates).forEach(([dateStr, rates]) => {
          if (rates && rates[currency]) {
            currencyData.push({
              date: dateStr,
              rate: 1 / rates[currency]
            })
          }
        })

        if (currencyData.length === 0) {
          throw new Error(`Exchange rate data for ${currency} is currently unavailable. Please try again later.`)
        }

        // Sort by date to ensure chronological order
        currencyData.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
        this.fiatDataCache[currency] = currencyData
      })

      // Verify we have data for both currencies
      if (!this.fiatDataCache['EUR'] || !this.fiatDataCache['USD']) {
        const missingCurrencies = []
        if (!this.fiatDataCache['EUR']) missingCurrencies.push('EUR')
        if (!this.fiatDataCache['USD']) missingCurrencies.push('USD')
        throw new Error(`Exchange rate data for ${missingCurrencies.join(' and ')} is currently unavailable. Please try again later.`)
      }

      this.fiatDataCacheTimestamp = now
      console.log('EUR and USD data successfully fetched from Frankfurter.dev')
    } catch (error) {
      // Handle different types of errors
      if (axios.isAxiosError(error)) {
        // Handle network and timeout errors
        if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
          throw new Error('Connection timeout. Please check your internet connection and try again.')
        }
        if (error.code === 'NETWORK_ERROR' || error.message.includes('Network Error')) {
          throw new Error('Network error. Please check your internet connection and try again.')
        }
        if (error.response && error.response.status >= 500) {
          throw new Error('Exchange rate service is temporarily down. Please try again later.')
        }
        if (error.response && error.response.status === 429) {
          throw new Error('Too many requests. Please wait a moment and try again.')
        }
        if (error.response && error.response.status === 404) {
          throw new Error('Exchange rate data not found. Please try again later.')
        }
        // Generic axios error
        throw new Error('Unable to fetch exchange rate data. Please check your connection and try again.')
      }
      // Re-throw existing error messages or create default
      throw error instanceof Error ? error : new Error('Unable to fetch exchange rate data. Please try again later.')
    }
  }

  // Get historical data for fiat currencies from cached year-long dataset
  async getFiatHistoricalData(
    targetCurrency: string,
    days: number
  ): Promise<ExchangeRateData[]> {
    // Ensure we have the fiat data cache populated
    await this.fetchAllFiatData()

    const cachedData = this.fiatDataCache[targetCurrency]
    if (!cachedData || cachedData.length === 0) {
      throw new Error(`No exchange rate data available for ${targetCurrency}. Please check your internet connection and try again.`)
    }

    // For 1-day chart, we need hourly-like data but Frankfurter only provides daily data
    // We'll return the last few days of data to provide some granularity
    if (days === 1) {
      // Return last 7 days of data for 1-day view to show some variation
      const lastWeekData = cachedData.slice(-7)
      if (lastWeekData.length === 0) {
        throw new Error(`Insufficient data available for ${targetCurrency}`)
      }
      return lastWeekData
    }

    // Calculate cutoff date for filtering
    const cutoffDate = new Date()
    cutoffDate.setDate(cutoffDate.getDate() - days)
    const cutoffDateStr = cutoffDate.toISOString().split('T')[0]

    // Filter cached data to the requested timeframe
    const filteredData = cachedData.filter(item => item.date >= cutoffDateStr)

    if (filteredData.length === 0) {
      throw new Error(`No exchange rate data available for ${targetCurrency} in the requested timeframe. Data may be too recent or the timeframe too narrow.`)
    }

    return filteredData
  }

  // Get historical data for cryptocurrencies using CoinGecko API
  // Fetch appropriate dataset (30 days for short term, 365 days for long term) to minimize API calls
  async getCryptoHistoricalData(
    coinId: string,
    days: number
  ): Promise<ExchangeRateData[]> {
    try {
      // Determine optimal fetch period: 30 days for 1D/1W/1M, 365 days for 6M/1Y
      const fetchDays = days <= 30 ? 30 : 365

      const response = await coinGeckoApi.get<CoinGeckoHistoryResponse>(
        `/coins/${coinId}/market_chart`,
        {
          params: {
            vs_currency: 'czk',
            days: fetchDays
          }
        }
      )

      if (!response.data.prices || response.data.prices.length === 0) {
        throw new Error(`No cryptocurrency price data available for ${coinId.toUpperCase()}. Please try again later.`)
      }

      // Convert timestamps to dates with hour precision
      const allData = response.data.prices
        .map(([timestamp, price]) => ({
          date: new Date(timestamp).toISOString(),
          rate: price // Use exact API values without rounding
        }))

      // Filter data based on requested timeframe if it's shorter than what we fetched
      if (days >= fetchDays) {
        return allData
      }

      // Calculate cutoff date for filtering
      const cutoffDate = new Date()

      // For 1-day charts, we want data from the last 24 hours
      if (days === 1) {
        cutoffDate.setHours(cutoffDate.getHours() - 24)
      } else {
        cutoffDate.setDate(cutoffDate.getDate() - days)
      }

      const cutoffISO = cutoffDate.toISOString()

      // Filter to only include data within the requested timeframe
      const filteredData = allData.filter(item => item.date >= cutoffISO)

      if (filteredData.length === 0) {
        throw new Error(`No cryptocurrency data available for ${coinId.toUpperCase()} in the requested timeframe. Please try a longer time period.`)
      }

      return filteredData
    } catch (error) {
      // Handle different types of errors
      if (axios.isAxiosError(error)) {
        // Handle network and timeout errors
        if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
          throw new Error('Connection timeout. Please check your internet connection and try again.')
        }
        if (error.code === 'NETWORK_ERROR' || error.message.includes('Network Error')) {
          throw new Error('Network error. Please check your internet connection and try again.')
        }
        if (error.response && error.response.status >= 500) {
          throw new Error('Cryptocurrency service is temporarily down. Please try again later.')
        }
        if (error.response && error.response.status === 429) {
          throw new Error('Too many requests to cryptocurrency service. Please wait a moment and try again.')
        }
        if (error.response && error.response.status === 404) {
          throw new Error('Cryptocurrency data not found. Please try again later.')
        }
        // Generic axios error
        throw new Error('Unable to fetch cryptocurrency data. Please check your connection and try again.')
      }
      // Re-throw existing error messages or create default
      throw error instanceof Error ? error : new Error('Unable to fetch cryptocurrency data. Please try again later.')
    }
  }

  // Get cache status for debugging/monitoring
  getCacheStatus(): { hasFiatCache: boolean; cacheAge: string; currencies: string[] } {
    const now = Date.now()
    const ageMs = this.fiatDataCacheTimestamp ? now - this.fiatDataCacheTimestamp : 0
    const ageMinutes = Math.floor(ageMs / (60 * 1000))

    return {
      hasFiatCache: Object.keys(this.fiatDataCache).length > 0,
      cacheAge: this.fiatDataCacheTimestamp ? `${ageMinutes} minutes ago` : 'No cache',
      currencies: Object.keys(this.fiatDataCache)
    }
  }
}

export const exchangeRateService = new ExchangeRateService()
